import { UserFreeBetQuery } from '../../../libs/graphql/baseAppAPI/queries/__generated__/user-free-bet-get.generated';
import { UserFreeSpinQuery } from '../../../libs/graphql/baseAppAPI/queries/__generated__/user-free-spin-get.generated';
import { UserBonusesQuery } from '../../../libs/graphql/baseAppAPI/queries/__generated__/user-promo-collection.generated';
import { CustomField } from '../../../models/bonuses.model';
import { UserProfileBonusStatus } from '../../../types.__generated__';
import { ProductType } from './PromotionsBlock/types';

export enum CarouselType {
  BOX = 'box',
  WIDE = 'wide',
  DOUBLE = 'double',
}

export interface CommonBonusItem {
  id: number;
  reason: string;
  canBeClaimed?: boolean;
  startDate?: string | null;
  endDate?: string;
  activedDate?: string;
  expireDateLabel?: string;
  endDateToQualify?: string | null;
  bonusType: string;
  status?: string;
  bonusAmount?: number;
  isVisibleToLoggedOut?: boolean;
  description?: string;
  infoText?: string;
  images: {
    default: {
      url: string | null;
    };
  };
  customFields?: CustomField[];
  promoCode: string;
  promoButtonText?: string;
  shortDescription?: string;
  shortDescriptionTC?: string;
  imgAlt?: string;
  pageUrl?: string;
  imgPath?: string;
  imgMobilePath?: string;
  promotionTitle?: string;
  isHiddenTitle?: boolean;
  displayName?: string;
  priority?: number;
  currentAccumulation?: number | null;
  targetAccumulation?: number | null;
  expiryDate?: string;
  promotionCTAType?: string;
  manualURL?: string;
  categories?: string[];
  products?: ProductType[];
}

export type OnCallback = (promoCode: string) => void;

export interface IPromotionMainBlockState {
  show: boolean;
  loading: boolean;
  isActivateError: boolean;
}

export interface PromotionTermsConditionsProps {
  titleTC: string;
  descriptionTC: string;
}

export type Products = (string | null | undefined)[];

export type ParsedPromo = {
  id?: string;
  isFreeBet?: boolean;
  bonusType?: string;
  tagId?: string | null;
  status?: string | null;
  bonusId?: string | null;
  dmBonusId?: string | null;
  dmMaxAmount?: string | null;
  startDate?: string;
  endDate?: string;
  title?: string;
  subtitle?: string;
  description?: string;
  ctaCopy?: string | null;
  ctaUrl?: string | null;
  imgPath?: string;
  wideImgPath?: string;
  imgAlt?: string;
  pageUrl?: string;
  pageSubUrl?: string;
  promoCode?: string;
  promoButtonText?: string;
  promotionCTAType?: string;
  products?: Products;
  expireDateLabel?: string;
  successCTAGameInformation?: {
    gameId: string;
  };
  wageredAmount?: string;
  wageringRequirementAmount?: string;
  balanceAmount?: string;
  convertedAmount?: null | string;
  expiresOn?: string;
  createdOn?: string;
  hideTerms?: boolean;
  cancellable?: boolean;
  promoType?: 'depositMatch' | 'freeBet' | 'freeSpin' | 'network' | string | null;
  ordering?: number | null;
  carouselType?: string | null;
  magicXSlug?: string | null;
  magicXCta?: string | null;
  rewardId?: string | null;
  reward_type?: string | null;
  type?: string;
};

export type RewardPromo = {
  reward_ID?: string | null;
  reward_type?: string | null;
};

export type GigPromo = {
  id: string;
  bonusTemplateId: string;
  expiresOn: string;
  createdOn: string;
  name: string;
  status: string;
  lockedAmount: string;
  grantedAmount: string;
  wageredAmount: string;
  wageringRequirementAmount: string;
  balanceAmount: string;
  convertedAmount: null | string;
} | null;

export type FreeSpinPromo = {
  id: string;
  freeSpinsTemplateId: string;
  bonusTemplateId: string;
  status: string;
  expiresOn: string;
};

export type FreeBetPromo = {
  id: string;
  freeBetTemplateId: string;
  bonusTemplateId: string;
  status: string;
  expiresOn: string;
  grantedAmount: string;
  convertedAmount: string;
  balanceAmount: string;
  conversionMaxAmount: string;
};

export type MagicXPromo = {
  expiry: string;
  id: string;
  milestones: {
    level: string;
    value: string;
  }[];
  slug: string;
  start: string;
  type: string;
  winAmount: string;
  winFreespins: string;
  achievementValue: string;
  values: {
    gb: {
      cta: { text: string }[];
    };
  };
};

export type GigCommonPromo = GigPromo | FreeSpinPromo | FreeBetPromo | MagicXPromo;
export type UserGigQuery = UserBonusesQuery | UserFreeBetQuery | UserFreeSpinQuery;

export const activePromoStatuses: string[] = [
  UserProfileBonusStatus.InProgress,
  UserProfileBonusStatus.Claimed,
  UserProfileBonusStatus.Assigned,
];
export const availablePromoStatuses: string[] = [UserProfileBonusStatus.Available];

export type ExistedPromos = {
  isDeposit: boolean;
  isFreeBet: boolean;
  isFreeSpins: boolean;
  isWagerAndGet: boolean;
};

export enum PromoTypes {
  NETWORK = 'network',
  DEPOSIT_MATCH = 'deposit_match',
  FREE_BET = 'free_bet',
  FREE_SPINS = 'free_spins',
  WAGER_AND_GET = 'wager_&_get',
}

export enum PromotionPromptType {
  DEFAULT = 'default',
  DEPOSIT = 'deposit',
  CANCELLATION = 'cancellation',
  ERROR = 'error',
}

export type OtpErrorResponse = {
  name: string;
  message: string;
};

enum PromotionMagicXTypes {
  BONUS_ROUND = 0,
  DEPOSIT_COUNT = 1,
  TURNOVER = 2,
  DEPOSIT_AMOUNT = 3,
  SPIN_COUNT = 5,
  SPORTSBOOK_NUMBER_SETTLED_BETS = 8,
  SPORTSBOOK_NUMBER_SETTLED_BETS_REAL = 18,
  SPORTSBOOK_TURNOVER = 12,
  SPORTSBOOK_TURNOVER_REAL = 19,
  REAL_TURNOVER = 14,
  REAL_SPIN_COUNT = 15,
  BONUS_TURNOVER = 16,
  BONUS_SPIN_COUNT = 17,
}

export const countBasedPromoTypes = [
  PromotionMagicXTypes.BONUS_ROUND,
  PromotionMagicXTypes.DEPOSIT_COUNT,
  PromotionMagicXTypes.SPIN_COUNT,
  PromotionMagicXTypes.SPORTSBOOK_NUMBER_SETTLED_BETS,
  PromotionMagicXTypes.SPORTSBOOK_NUMBER_SETTLED_BETS_REAL,
  PromotionMagicXTypes.BONUS_SPIN_COUNT,
];
